import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getSiteSettings, getNavigation, getFooter } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";
import { normalizeMenu, footerServiceLinks, isExternal } from "../lib/siteNav";
import logoFullColor from "@/assets/logo-fullcolor.svg";

// Footer (EP-001 v1.1: D22, D26). Four columns that link only to pages and profiles that
// exist, the tagline once as the brand sign-off beside the logo, and the legal line.

const TAGLINE = "Connecting you to what's next.";

const DEFAULT_LEGAL_LINKS = [
    { label: "Privacy Policy", url: "/privacy-policy" },
    { label: "Accessibility", url: "/accessibility" },
];

const FooterLink = ({ link }) => {
    const className = "text-sm text-brand-muted transition-colors duration-150 hover:text-brand-text";
    if (isExternal(link.url)) {
        return (
            <a href={link.url} target="_blank" rel="noopener noreferrer" className={className}>
                {link.label}
                <span className="sr-only"> (opens in a new tab)</span>
            </a>
        );
    }
    // Hash links (e.g. /#why) need a full anchor so the browser scrolls to the section.
    if (link.url.includes("#")) {
        return (
            <a href={link.url} className={className}>
                {link.label}
            </a>
        );
    }
    return (
        <Link to={link.url} className={className}>
            {link.label}
        </Link>
    );
};

export const Footer = () => {
    const [settings, setSettings] = useState(() => peek(getSiteSettings));
    const [nav, setNav] = useState(() => peek(getNavigation));
    const [footer, setFooter] = useState(() => peek(getFooter));

    useEffect(() => {
        let active = true;
        getSiteSettings()
            .then((data) => active && data && setSettings(data))
            .catch(() => {});
        getNavigation()
            .then((data) => active && data && setNav(data))
            .catch(() => {});
        getFooter()
            .then((data) => active && data && setFooter(data))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const menu = normalizeMenu(nav?.menuItems);
    const ctaUrl = nav?.primaryCta?.url || "/contact";
    const ctaLabel = nav?.primaryCta?.label || "Schedule a Conversation";

    const social = footer?.showSocialLinks === false ? [] : [
        settings?.linkedinUrl && { label: "LinkedIn", url: settings.linkedinUrl },
        settings?.facebookUrl && { label: "Facebook", url: settings.facebookUrl },
    ].filter(Boolean);

    const columns = [
        {
            heading: "EasiLinked",
            links: [
                { label: "Enterprise", url: "/enterprise" },
                { label: "Healthcare Secure™", url: "/healthcare-secure" },
                { label: "Why EasiLinked", url: "/#why" },
                { label: "About", url: "/about" },
            ],
        },
        { heading: "Services", links: footerServiceLinks(menu) },
        {
            heading: "The EasiLinked Perspective",
            links: [
                { label: "Articles", url: "/perspective" },
                { label: "Field Notes", url: "/perspective#perspective-notes" },
            ],
        },
        {
            heading: "Let's Connect",
            links: [{ label: ctaLabel, url: ctaUrl }, { label: "Contact", url: "/contact" }, ...social],
        },
    ];

    const legalLinks = footer?.legalLinks?.length
        ? footer.legalLinks.filter((l) => l?.label && l?.url?.startsWith("/"))
        : DEFAULT_LEGAL_LINKS;
    const tagline = footer?.tagline?.trim() || TAGLINE;

    return (
        <footer data-testid="footer" className="border-t border-brand-line bg-brand-bg">
            <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
                <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-12">
                    <div className="col-span-2 lg:col-span-4">
                        <Link to="/" className="inline-flex" aria-label="EasiLinked home">
                            {/* Full-color logo in both themes; in dark mode it sits on a white plate so the Dark Blue lettering stays legible (D32). */}
                            <span className="-ml-2 inline-flex rounded-control bg-white px-2 py-1.5">
                                <img
                                    src={logoFullColor}
                                    alt="EasiLinked — Managed Network & IT Services"
                                    width="176"
                                    height="40"
                                    loading="lazy"
                                    className="block h-10 w-auto"
                                />
                            </span>
                        </Link>
                        <p data-testid="footer-tagline" className="mt-5 font-heading text-xl text-brand-text">
                            {tagline}
                        </p>
                        {footer?.footerText && (
                            <p className="mt-3 max-w-xs text-sm leading-relaxed text-brand-muted">{footer.footerText}</p>
                        )}
                    </div>

                    {columns.map((col) => (
                        <div key={col.heading} className="lg:col-span-2">
                            <h2 className="text-sm font-semibold text-brand-text">{col.heading}</h2>
                            <ul className="mt-4 space-y-3">
                                {col.links.map((link) => (
                                    <li key={`${col.heading}-${link.label}`}>
                                        <FooterLink link={link} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-14 flex flex-col gap-3 border-t border-brand-line pt-8 text-xs text-brand-muted sm:flex-row sm:items-center sm:gap-6">
                    <p>{`© ${new Date().getFullYear()} ${settings?.companyName || "EasiLinked"}. All rights reserved.`}</p>
                    <nav aria-label="Legal" className="flex gap-5">
                        {legalLinks.map((l) => (
                            <Link key={l.url} to={l.url} className="transition-colors duration-150 hover:text-brand-text">
                                {l.label}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>
        </footer>
    );
};
